<template>
  <div class="user-balance-movement-regularization-page">
    <a-row type="flex" justify="space-between">
      <a-col :lg="8" :md="8" :sm="10" :xs="24">
        <h2 class="list-title">
          <font-awesome-icon
            class="anticon list-icon"
            :icon="['fas', 'scale-balanced']"
          />
          <span>{{ labels.title }}</span>
        </h2>
        <p class="list-subtitle">{{ labels.subtitle }}</p>
      </a-col>
    </a-row>

    <a-row type="flex" :gutter="[24, 24]">
      <a-col :span="24">
        <a-card>
          <a-row type="flex" justify="start" :gutter="24">
            <a-col :lg="8" :md="8" :sm="24" :xs="24">
              <SelectPagination
                v-model="form.user"
                :fetchService="fetchUsers"
                :placeholder="labels.filters.user.placeholder"
                :labelKey="'fullname'"
                :valueKey="'id'"
                :label="labels.filters.user.label"
                :disabledDefaultFetchOptions="false"
                :allowClear="true"
                :customStyle="{ width: '100%' }"
              />
            </a-col>
            <a-col
              :style="{
                paddingTop: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }"
            >
              <a-button
                type="primary"
                class="add-button"
                @click="loadRegularization"
                :disabled="loading || !form.user"
                :loading="loading"
                :title="labels.userRequired"
              >
                {{ labels.filters.search }}
              </a-button>
            </a-col>
            <a-col
              v-if="requested && !loading && form.dataSource.length"
              :style="{
                paddingTop: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }"
            >
              <a-button
                type="danger"
                class="add-button"
                @click="confirmRegularization"
                :disabled="submitting"
                :loading="submitting"
              >
                {{ labels.form.btnRegularize }}
              </a-button>
            </a-col>
          </a-row>
          <template v-if="requested && !loading && form.dataSource.length">
            <a-divider orientation="left">{{ labels.balanceTitle }}</a-divider>
            <div class="balance-container">
              <a-statistic
                :title="labels.currentBalanceLabel"
                :value="current_balance"
                style="margin-right: 50px"
                :value-style="{ color: 'var(--warning)' }"
              />
              <a-statistic
                :title="labels.newBalanceLabel"
                :precision="0"
                :value="new_balance"
                :value-style="{ color: 'var(--success)' }"
              />
            </div>
          </template>
        </a-card>
      </a-col>
      <a-col v-if="requested && !loading" :span="24">
        <StandardTable
          ref="standardTable"
          :loading="loading"
          :data="form.dataSource"
          :columns="columns"
          :locale="locale"
          v-bind="!isMobile && { scroll: { x: true } }"
          :paginate="false"
          :customRow="handleCustomRow"
        />
      </a-col>
    </a-row>
  </div>
</template>

<script>
import _ from "lodash";
import labels from "@/utils/labels";
import StandardTable from "../../../components/core/table/StandardTable.vue";
import SelectPagination from "@/components/core/SelectPagination/index.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import accounting from "accounting";
import { DATE_FORMAT, adminPageTitle } from "../../../utils/utils";
const initialState = {
  dataSource: [],
  user: null,
  regularize: false,
  balance: 0,
};
export default {
  components: { StandardTable, SelectPagination },
  name: "UserBalanceMovementRegularization",
  metaInfo: {
    title: adminPageTitle("Regularización de Movimientos de Balance"),
  },
  data() {
    return {
      labels: labels.userbalancemovementRegularization,
      form: _.cloneDeep(initialState),
      current_balance: 0,
      new_balance: 0,
      loading: false,
      submitting: false,
      requested: false,
      errors: [],
    };
  },
  filters: {
    ucwords(value) {
      if (!value) return "";
      value = value.toString();
      return value
        .split(" ")
        .map((item) => _.capitalize(item))
        .join(" ");
    },
  },
  methods: {
    ...mapActions("users", [
      "fetchUserBalanceMovementRegularization",
      "regularizeUserBalanceMovements",
      "fetchUsers",
    ]),
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
    async loadRegularization() {
      try {
        this.loading = true;
        const { user } = this.form;
        if (!user) {
          this.$message.error(this.labels.errors.user);
          return;
        }
        const response = await this.fetchUserBalanceMovementRegularization({
          user: user.id,
        });
        if (response) {
          const { data, current_balance = 0, new_balance = 0 } = response.data;
          this.form.dataSource = data;
          this.current_balance = current_balance;
          this.new_balance = new_balance;
          if (this.new_balance >= 0) {
            this.form.balance = this.new_balance;
          }
        }
      } catch (error) {
        this.$message.error(this.labels.loadError);
      } finally {
        this.loading = false;
        this.requested = true;
      }
    },
    async handleRegularization() {
      try {
        this.submitting = true;
        const { user, balance, regularize, dataSource = [] } = this.form;

        if (!user?.id) {
          this.$message.error(this.labels.errors.user);
          return;
        }

        if (dataSource.length === 0) {
          this.$message.error(this.labels.errors.dataSource);
          return;
        }

        await this.regularizeUserBalanceMovements({
          user: {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
          },
          regularize,
          balance: regularize ? balance : 0,
          movements: dataSource.map((item) => ({
            id: item.id,
            current_balance: item.current_balance_reg,
            amount: item.amount,
            new_balance: item.new_balance_reg,
          })),
        });

        this.$message.success(this.labels.success);

        this.form = _.cloneDeep(initialState);
        this.current_balance = 0;
        this.new_balance = 0;
        this.requested = false;
      } catch (error) {
        this.$message.error(this.labels.error);
      } finally {
        this.submitting = false;
      }
    },
    confirmRegularization() {
      try {
        this.$confirm({
          title: this.labels.confirmRegularization.title,
          content: (h) =>
            h(
              "div",
              {
                class: "confirm-regularization-content",
              },
              [
                h("p", this.labels.confirmRegularization.subtitle),
                h(
                  "a-form-item",
                  {
                    props: {
                      labelCol: { span: 24 },
                      help: this.labels.confirmRegularization.help,
                      validateStatus: this.form.regularize ? "warning" : "",
                      ...(this.errors.length && {
                        validateStatus: "error",
                        help: this.errors[0],
                      }),
                    },
                  },
                  [
                    h(
                      "a-checkbox",
                      {
                        props: {
                          checked: this.form.regularize,
                        },
                        on: {
                          change: (event) => {
                            this.form.regularize = event.target.checked;
                          },
                        },
                      },
                      [h("span", this.labels.form.regularize.label)]
                    ),
                    h("a-input", {
                      props: {
                        placeholder: this.labels.form.balance.placeholder,
                        modelValue: this.form.balance,
                        defaultValue: this.form.balance,
                        allowClear: true,
                        type: "number",
                        min: 0,
                        readonly: !this.form.regularize,
                        disabled: !this.form.regularize,
                        class: "input-number",
                      },
                      on: {
                        change: (event) => {
                          if (this.errors.length) this.errors = [];
                          this.form.balance = parseFloat(
                            event.target.value || 0
                          );
                        },
                      },
                      style: {
                        width: "100%",
                      },
                    }),
                  ]
                ),
              ]
            ),
          okText: this.labels.confirmRegularization.confirmButtonText,
          okType: "danger",
          icon: "warning",
          cancelText: this.labels.confirmRegularization.cancelButtonText,
          visible: true,
          okButtonProps: {
            disabled:
              this.submitting || (this.form.regularize && !this.form.balance),
            loading: this.submitting,
          },
          onOk: async () => {
            this.errors = [];
            await this.handleRegularization();
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  computed: {
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    ...mapState("layout", ["screenWidth"]),
    isMobile() {
      return this.screenWidth < 600;
    },
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.date,
          dataIndex: "date",
          key: "date",
          hidden: this.isMobile,
          width: "20%",
          customRender: (text) => {
            return this.$moment(text).format(DATE_FORMAT.MOMENT_DATE_TIME);
          },
        },
        {
          title: this.labels.table.columns.type.title,
          dataIndex: "type",
          key: "type",
          hidden: this.isMobile,
          width: "20%",
          customRender: (text) => {
            return this.labels.table.columns.type[text.toUpperCase()];
          },
        },
        {
          title: this.labels.table.columns.currentBalance,
          dataIndex: "current_balance",
          key: "current_balance",
          hidden: this.isMobile,
          width: "20%",
          customRender: (text, record) => {
            const amount = accounting.formatNumber(text || 0, 0, ",", ".");
            const regularize = accounting.formatNumber(
              record.current_balance_reg || 0,
              0,
              ",",
              "."
            );
            const notEqual = amount !== regularize;
            return (
              <div class={"amount-label"}>
                {amount !== regularize ? <span>{amount}</span> : ""}
                <span class={notEqual ? "success" : "not-line-through"}>
                  {regularize}
                </span>
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.amount,
          dataIndex: "amount",
          key: "amount",
          hidden: this.isMobile,
          width: "20%",
          customRender: (text, record) => {
            return (
              <span>
                <span>{accounting.formatNumber(text || 0, 0, ",", ".")}</span>
                <a-tooltip
                  style={{ marginLeft: "6px" }}
                  placement="top"
                  title={record.description}
                  trigger="click"
                >
                  <a-icon type="info-circle" style={{ color: "#faad14" }} />
                </a-tooltip>
              </span>
            );
          },
        },
        {
          title: this.labels.table.columns.newBalance,
          dataIndex: "new_balance",
          key: "new_balance",
          hidden: this.isMobile,
          width: "20%",
          customRender: (text, record) => {
            const amount = accounting.formatNumber(text || 0, 0, ",", ".");
            const regularize = accounting.formatNumber(
              record.new_balance_reg || 0,
              0,
              ",",
              "."
            );
            const notEqual = amount !== regularize;
            return (
              <div class={"amount-label"}>
                {notEqual ? <span>{amount}</span> : ""}
                <span class={notEqual ? "success" : "not-line-through"}>
                  {regularize}
                </span>
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.movements,
          dataIndex: "movements",
          key: "movements",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_, record) => {
            // Card item design
            const color = record?.type === "DEBIT" ? "#faad14" : "#52c41a";
            const type = record?.type
              ? this.labels.table.columns.type[record?.type?.toUpperCase()]
              : "-";
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(
                        this.$moment(record.date).format(
                          DATE_FORMAT.MOMENT_DAY_MONTH
                        )
                      )}
                    </span>
                    <small>
                      {this.$moment(record.date)
                        .format(DATE_FORMAT.MOMENT_TIME)
                        .toUpperCase()}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-reference">
                      <span>{type}</span>
                    </div>
                    <div class="list-item-head-content-number">
                      <span>{record.number}</span>
                    </div>
                    <div class="list-item-head-content-reference">
                      <span>
                        {record.reference}
                        <a-tooltip
                          placement="top"
                          title={record.description}
                          trigger="click"
                          class="list-item-head-content-reference-tooltip"
                        >
                          <a-icon
                            class="list-item-head-content-reference-tooltip-icon"
                            type="info-circle"
                            style={{ color: "#faad14" }}
                          />
                        </a-tooltip>
                      </span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    <div
                      title={this.labels.table.columns.currentBalance}
                      class="list-item-head-footer-amount"
                    >
                      <span>
                        {accounting.formatNumber(
                          record.current_balance_reg,
                          0,
                          ",",
                          "."
                        )}
                      </span>
                    </div>
                    <div
                      title={this.labels.table.columns.amount}
                      class="list-item-head-footer-amount"
                    >
                      <span>
                        {accounting.formatNumber(record.amount, 0, ",", ".")}
                      </span>
                    </div>
                    <div
                      title={this.labels.table.columns.newBalance}
                      class="list-item-head-footer-amount"
                    >
                      <span>
                        {accounting.formatNumber(
                          record.new_balance_reg,
                          0,
                          ",",
                          "."
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
      ].filter((column) => !column?.hidden);
    },
    filtered() {
      return !_.isEqual(this.filters, initialState);
    },
    serverDateFormat() {
      return DATE_FORMAT.SERVER_DATE;
    },
  },
};
</script>
<style lang="scss">
@import "../../../assets/scss/variable";
.user-balance-movement-regularization-page {
  .list {
    &-title {
      margin-bottom: 10px;
      .list-icon {
        margin-right: 10px;
      }
    }
    &-subtitle {
      margin-bottom: 10px;
    }
  }
  .action-buttons {
    display: flex;
    justify-content: space-between;
    .action-button-view {
      color: color(primary);
    }
    .action-button-cancel {
      color: color(danger);
    }
  }
  @media screen and (max-width: $screen-mobile) {
    .row-reverse {
      flex-direction: column-reverse;
    }
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 22%;
        border-right: 4px solid color(border);
        .success {
          color: color(success);
        }
        .warning {
          color: color(warning);
        }
        .danger {
          color: color(danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 53%;
        overflow: hidden;
        &-number {
          span {
            font-size: 14px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
        &-type {
          span {
            font-size: 12px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
        }
        &-reference {
          span {
            font-size: 14px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
          &-tooltip {
            margin-left: 6px;
            .anticon {
              font-size: 18px;
            }
          }
        }
        &-user {
          span {
            font-size: 12px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
        }
      }
      &-footer {
        max-width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        &-type {
          span {
            font-size: 12px;
            font-weight: 400;
          }
        }
        &-amount {
          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        &-balance {
          span {
            font-size: 12px;
            font-weight: 400;
          }
        }
      }
    }
  }
  .amount-label {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
    span {
      width: 100%;
    }
    span:not(.success):not(.not-line-through) {
      color: color(muted);
      text-decoration: line-through;
      font-size: 12px;
    }
    .success {
      color: color(success);
    }
  }
  .balance-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 20px;
  }
}
</style>
